import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TextField,
  Paper,
  TableContainer,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  Grid,
} from "@mui/material";
import {
  Search,
  Visibility,
  Download,
  Description,
  CalendarToday,
  Assignment,
  FilterList,
  Refresh,
} from "@mui/icons-material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { baseURL, config, requestID } from "../../../common/utils";
import { IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";

// Modern status colors with better variants
const getStatusConfig = (status) => {
  const configs = {
    pending: { color: "warning", label: "Pending", variant: "filled" },
    rejected: { color: "error", label: "Rejected", variant: "filled" },
    investigated: { color: "info", label: "Investigated", variant: "filled" },
    closed: { color: "success", label: "Closed", variant: "filled" },
    default: { color: "default", label: "Unknown", variant: "outlined" },
  };
  return configs[status] || configs.default;
};

// Skeleton loader component
const CaseSkeleton = () => (
  <Box sx={{ p: 2 }}>
    {[...Array(5)].map((_, index) => (
      <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box sx={{ width: "100%" }}>
          <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      </Box>
    ))}
  </Box>
);

const StaffPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [cases, setCases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const handleViewMore = (message) => {
    setSelectedMessage(message);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedMessage(null);
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseURL}whitsleBlowings/getMyBlowingCase`,
        payload,
        config
      );
      setCases(res.data.data);
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useIonViewWillEnter(() => {
    fetchStatus();
  }, []);

  // Function to extract file names from proof field
  const getFileNames = (proof) => {
    try {
      const files = JSON.parse(proof);
      if (Array.isArray(files)) {
        return files.map(file => {
          const cleanedPath = file.replace(/\\/g, "/");
          return cleanedPath.split("/").pop().toLowerCase();
        }).join(" ");
      }
    } catch (e) {
      return "";
    }
    return "";
  };

  // Function to search across all columns
  const searchCases = (cases, searchTerm) => {
    if (!searchTerm.trim()) return cases;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return cases.filter(c => {
      // Search in date
      const dateString = new Date(c.created_at).toLocaleDateString().toLowerCase();
      if (dateString.includes(lowerSearchTerm)) return true;
      
      // Search in message
      if (c.messages?.toLowerCase().includes(lowerSearchTerm)) return true;
      
      // Search in case name
      if (c.case_name?.toLowerCase().includes(lowerSearchTerm)) return true;
      
      // Search in status
      const statusConfig = getStatusConfig(c.status);
      if (statusConfig.label.toLowerCase().includes(lowerSearchTerm)) return true;
      
      // Search in file names
      const fileNames = getFileNames(c.proof);
      if (fileNames.includes(lowerSearchTerm)) return true;
      
      return false;
    });
  };

  const filteredCases = searchCases(cases, searchTerm);

  // Mobile card view component
  const CaseCard = ({ case: c }) => {
    const statusConfig = getStatusConfig(c.status);
    
    return (
      <Card sx={{ mb: 2, elevation: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Box>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                {c.case_name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                <CalendarToday sx={{ fontSize: 16, color: "text.secondary", mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {new Date(c.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={statusConfig.label}
              color={statusConfig.color}
              variant={statusConfig.variant}
              size="small"
            />
          </Box>

          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
            {c.messages.length > 100 ? `${c.messages.substring(0, 100)}...` : c.messages}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Tooltip title="View full message">
              <IconButton 
                size="small" 
                onClick={() => handleViewMore(c.messages)}
                color="primary"
              >
                <Visibility />
              </IconButton>
            </Tooltip>
            
            <Box sx={{ display: "flex", gap: 1 }}>
              {(() => {
                try {
                  const files = JSON.parse(c.proof);
                  if (Array.isArray(files) && files.length > 0) {
                    return (
                      <Tooltip title={`${files.length} document(s)`}>
                        <Chip
                          icon={<Description />}
                          label={files.length}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    );
                  }
                } catch (e) {
                  return null;
                }
                return null;
              })()}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ p: isSmallMobile ? 2 : 3 }}>
          {/* Header Section */}
          <Card sx={{ mb: 3, background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }}>
            <CardContent sx={{ color: "white", p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                    Case Management
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Monitor and track all whistleblowing cases
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: "white", color: theme.palette.primary.main, width: 56, height: 56 }}>
                  <Assignment sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>

          {/* Search and Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search across all columns (date, message, documents, case name, status)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <Button
                    startIcon={<FilterList />}
                    variant="outlined"
                    size="small"
                  >
                    Filter
                  </Button>
                  <Button
                    startIcon={<Refresh />}
                    variant="contained"
                    size="small"
                    onClick={fetchStatus}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Results Count */}
          {!loading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing {filteredCases.length} of {cases.length} cases
              {searchTerm && (
                <Typography component="span" variant="body2" color="primary.main" sx={{ ml: 1 }}>
                  • Searching in: Date, Message, Documents, Case Name, Status
                </Typography>
              )}
            </Typography>
          )}

          {/* Cases List */}
          {loading ? (
            <CaseSkeleton />
          ) : isMobile ? (
            // Mobile Card View
            <Box>
              {filteredCases.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm ? "No matching cases found" : "No cases available"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? "Try adjusting your search terms" : "No cases have been created yet"}
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                filteredCases.map((c) => <CaseCard key={c.id} case={c} />)
              )}
            </Box>
          ) : (
            // Desktop Table View
            <Card elevation={2}>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, background: theme.palette.background.default }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CalendarToday sx={{ fontSize: 18, mr: 1, color: "text.secondary" }} />
                          Date
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, background: theme.palette.background.default }}>
                        Message
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, background: theme.palette.background.default }}>
                        Documents
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, background: theme.palette.background.default }}>
                        Case Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, background: theme.palette.background.default }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography variant="h6" color="text.secondary">
                            No matching records found
                          </Typography>
                          {searchTerm && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              No results for "{searchTerm}" in date, message, documents, case name, or status
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCases.map((c) => {
                        const statusConfig = getStatusConfig(c.status);
                        return (
                          <TableRow key={c.id} hover sx={{ cursor: "pointer" }}>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <CalendarToday sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                                {new Date(c.created_at).toLocaleDateString()}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 300 }}>
                              <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                                {c.messages.length > 100 ? (
                                  <>
                                    {c.messages.substring(0, 100)}...
                                    <Box
                                      component="span"
                                      onClick={() => handleViewMore(c.messages)}
                                      sx={{
                                        color: "primary.main",
                                        cursor: "pointer",
                                        ml: 0.5,
                                        fontWeight: 600,
                                        "&:hover": { textDecoration: "underline" },
                                      }}
                                    >
                                      More
                                    </Box>
                                  </>
                                ) : (
                                  c.messages
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                try {
                                  const files = JSON.parse(c.proof);
                                  if (Array.isArray(files)) {
                                    return files.slice(0, 2).map((file, i) => {
                                      const cleanedPath = file.replace(/\\/g, "/");
                                      const fileName = cleanedPath.split("/").pop();
                                      return (
                                        <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <Description sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                                          <Tooltip title={fileName}>
                                            <a
                                              href={`${baseURL}${cleanedPath}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                textDecoration: "none",
                                                color: theme.palette.primary.main,
                                                fontSize: "0.875rem",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: 150,
                                                display: "block",
                                              }}
                                            >
                                              {fileName}
                                            </a>
                                          </Tooltip>
                                          {files.length > 2 && i === 1 && (
                                            <Tooltip title={`${files.length - 2} more files`}>
                                              <Chip label={`+${files.length - 2}`} size="small" sx={{ ml: 1 }} />
                                            </Tooltip>
                                          )}
                                        </Box>
                                      );
                                    });
                                  }
                                } catch (e) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      No documents
                                    </Typography>
                                  );
                                }
                                return null;
                              })()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {c.case_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={statusConfig.label}
                                color={statusConfig.color}
                                variant={statusConfig.variant}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

          {/* Message Modal */}
          <Dialog
            open={openModal}
            onClose={handleCloseModal}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 2 }
            }}
          >
            <DialogTitle sx={{ 
              background: `linear-gradient(45deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.main}10 100%)`,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Case Message Details
              </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <TextField
                value={selectedMessage}
                multiline
                fullWidth
                minRows={8}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  sx: { 
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                  }
                }}
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={handleCloseModal} 
                variant="contained"
                startIcon={<Visibility />}
              >
                Close Preview
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default StaffPage;